import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, History } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { DegreeRelationshipsService, type DegreeRelationship } from '@/services/modules/DegreeRelationshipsService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'
import { extractApiError } from '@/lib/api-errors'
import { DegreeRecoveryModal } from './DegreeRecoveryModal'

export function SettingsDegreeRelationshipsPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.degree_relationships.create')
  const canEdit = can('settings.degree_relationships.edit')
  const canDelete = can('settings.degree_relationships.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar grau de parentesco' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(DegreeRelationshipsService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: DegreeRelationship }>({ open: false })
  const [openDelete, setOpenDelete] = useState<DegreeRelationship | null>(null)
  const [openRecovery, setOpenRecovery] = useState(false)

  const exp = useDataTableExport({
    filename: 'graus-parentesco',
    service: DegreeRelationshipsService,
    filters: sf.filters,
    columns: [
      { header: 'Grau',   accessor: (d: DegreeRelationship) => d.degreeRelationship },
      { header: 'Gênero', accessor: (d: DegreeRelationship) => d.gender ?? '' },
      { header: 'Zelo (masculino)', accessor: (d: DegreeRelationship) => d.exportCodeMale ?? '' },
      { header: 'Zelo (feminino)',  accessor: (d: DegreeRelationship) => d.exportCodeFemale ?? '' },
      { header: 'Zelo (padrão)',    accessor: (d: DegreeRelationship) => d.exportCode ?? '' },
      { header: 'Status', accessor: (d: DegreeRelationship) => d.status },
    ],
  })

  async function handleSave(payload: Partial<DegreeRelationship>) {
    try {
      if (openForm.item) { await DegreeRelationshipsService.update(openForm.item.id, payload); toast.success('Grau atualizado.') }
      else { await DegreeRelationshipsService.create(payload); toast.success('Grau criado.') }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.') }
  }
  async function handleDelete() {
    if (!openDelete) return
    try { await DegreeRelationshipsService.destroy(openDelete.id); toast.success('Grau removido.'); setOpenDelete(null); setReload(v => v + 1) }
    // Grau com dependente ativo é bloqueado: o motivo vem do servidor.
    catch (e) { toast.error(extractApiError(e).message); setOpenDelete(null) }
  }

  // Sem coluna código. Primeira esquerda; demais center.
  const columns: DataTableColumn<DegreeRelationship>[] = [
    { key: 'degreeRelationship', header: 'Grau de parentesco', sortable: true, alwaysVisible: true, render: d => <span className="font-medium text-gray-800">{d.degreeRelationship}</span> },
    { key: 'gender',             header: 'Gênero', align: 'center', render: d => d.gender ?? '—', className: 'w-32' },
    { key: 'zelo', header: 'Código Zelo', align: 'center', render: d => {
      const partes = [d.exportCodeMale && `M: ${d.exportCodeMale}`, d.exportCodeFemale && `F: ${d.exportCodeFemale}`].filter(Boolean)
      if (!partes.length && !d.exportCode) return <span className="text-xs text-gray-400">—</span>
      return (
        <div className="leading-tight">
          {partes.length > 0 && <div className="text-xs text-gray-700">{partes.join(' · ')}</div>}
          <div className="text-[11px] text-gray-500">padrão: {d.exportCode || 'vazio'}</div>
        </div>
      )
    } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: d => (
        <StatusDot value={d.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Graus de parentesco"
        subtitle="Catálogo ANS"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Workflow' }, { label: 'Graus de parentesco' }]}
        actions={<>
          {canEdit && <Button variant="secondary" onClick={() => setOpenRecovery(true)} iconLeft={<History size={14} />}>Graus excluídos</Button>}
          {canCreate && <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo grau</Button>}
        </>}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.workflow.degreerelationships"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={d => d.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(d) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: d }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(d) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <DegreeForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={handleSave} />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover grau de parentesco?"
        description={`Remover "${openDelete?.degreeRelationship}"? Ele sai das listas, mas quem já tem este grau continua com ele. Se algum dependente ativo usa o grau, a remoção é bloqueada.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {openRecovery && <DegreeRecoveryModal onClose={() => setOpenRecovery(false)} onDone={() => setReload(v => v + 1)} />}
      {sf.drawer}
    </>
  )
}

function DegreeForm({ open, item, onClose, onSave }: {
  open: boolean; item?: DegreeRelationship; onClose: () => void
  onSave: (payload: Partial<DegreeRelationship>) => Promise<void>
}) {
  const [degreeRelationship, setDegreeRelationship] = useState('')
  const [exportCode, setExportCode] = useState('')
  const [exportCodeMale, setExportCodeMale] = useState('')
  const [exportCodeFemale, setExportCodeFemale] = useState('')
  const [gender, setGender] = useState<'MASCULINO' | 'FEMININO' | 'OUTRO' | ''>('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setDegreeRelationship(item?.degreeRelationship ?? '')
    setExportCode(item?.exportCode ?? '')
    setExportCodeMale(item?.exportCodeMale ?? '')
    setExportCodeFemale(item?.exportCodeFemale ?? '')
    setGender((item?.gender as never) ?? '')
    setStatus(item?.status ?? 'ATIVO')
  }, [open, item])

  async function submit() {
    setSaving(true)
    await onSave({
      degreeRelationship,
      exportCode: exportCode.trim() || null,
      exportCodeMale: exportCodeMale.trim() || null,
      exportCodeFemale: exportCodeFemale.trim() || null,
      gender: gender || null,
      status,
    } as Partial<DegreeRelationship>)
    setSaving(false)
  }
  return (
    <Modal open={open} onClose={onClose}
      title={item ? 'Editar grau de parentesco' : 'Novo grau de parentesco'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!degreeRelationship.trim()}>Salvar</Button></>}
    >
      <div className="space-y-3">
        <Input label="Descrição" required value={degreeRelationship} onChange={e => setDegreeRelationship(e.target.value)} placeholder="Filho(a)" />
        <div className="rounded-md p-3 ring-1 ring-gray-200">
          <p className="text-sm font-medium text-gray-800">Código Zelo (GRAU)</p>
          <p className="mb-3 text-xs text-gray-500">
            Vai na coluna GRAU do relatório da Zelo, conforme o gênero do dependente. Sem código para o gênero (ou sem gênero no cadastro), vale o padrão.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input label="Masculino" value={exportCodeMale} onChange={e => setExportCodeMale(e.target.value)} placeholder="Ex.: ESPOSO" />
            <Input label="Feminino" value={exportCodeFemale} onChange={e => setExportCodeFemale(e.target.value)} placeholder="Ex.: ESPOSA" />
            <Input label="Padrão" value={exportCode} onChange={e => setExportCode(e.target.value)} placeholder="Ex.: CONJUGE"
              hint="Em branco: o GRAU sai vazio." />
          </div>
        </div>
        <Select
          label="Gênero"
          value={gender}
          onChange={e => setGender(e.target.value as 'MASCULINO' | 'FEMININO' | 'OUTRO' | '')}
          options={[
            { value: '', label: 'Indiferente' },
            { value: 'MASCULINO', label: 'Masculino' },
            { value: 'FEMININO',  label: 'Feminino' },
            { value: 'OUTRO',     label: 'Outro' },
          ]}
        />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
